import React from 'react';
import Svg, { Path, Circle, Rect, Polygon, Polyline, Line, G } from 'react-native-svg';
import { Palette } from '../theme/tokens';

export type IconName =
  | 'back'
  | 'settings'
  | 'pause'
  | 'play'
  | 'close'
  | 'check'
  | 'refresh-cw'
  | 'upload'
  | 'plus'
  | 'copy'
  | 'send'
  | 'download'
  | 'transfers'
  | 'info'
  | 'file'
  | 'folder'
  | 'zap'
  | 'lock'
  | 'users'
  | 'globe'
  | 'link'
  | 'magnet'
  | 'calendar'
  | 'camera'
  | 'chevron-right'
  | 'chevron-down'
  | 'trash'
  | 'x-twitter'
  | 'github'
  | 'discord'
  | 'telegram'
  | 'reddit'
  | 'linkedin'
  | 'menu'
  | 'image'
  | 'video'
  | 'music'
  | 'file-text'
  | 'archive'
  | 'eye'
  | 'logo';

interface IconProps {
  name: IconName;
  size?: number;
  color?: string;
  strokeWidth?: number;
}

/**
 * Standard Lucide / Feather Vector Icon Pack
 * High-precision 24x24 geometric vector icons — minimalist, pixel-perfect SVG icon system.
 */
export const Icon: React.FC<IconProps> = ({ name, size = 24, color = Palette.white, strokeWidth = 2 }) => {
  const renderIconContent = () => {
    switch (name) {
      case 'menu':
        return (
          <>
            <Line x1="3" y1="12" x2="21" y2="12" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
            <Line x1="3" y1="6" x2="21" y2="6" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
            <Line x1="3" y1="18" x2="21" y2="18" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
          </>
        );
      case 'back':
        // Lucide: arrow-left
        return <Path d="M19 12H5M12 19l-7-7 7-7" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />;

      case 'settings':
        // Lucide: settings
        return (
          <>
            <Circle cx="12" cy="12" r="3" stroke={color} strokeWidth={strokeWidth} />
            <Path
              d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"
              stroke={color}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </>
        );

      case 'pause':
        // Lucide: pause
        return (
          <>
            <Rect x="6" y="4" width="4" height="16" rx="1" stroke={color} strokeWidth={strokeWidth} fill={color} />
            <Rect x="14" y="4" width="4" height="16" rx="1" stroke={color} strokeWidth={strokeWidth} fill={color} />
          </>
        );

      case 'play':
        // Lucide: play
        return <Polygon points="5 3 19 12 5 21 5 3" fill={color} stroke={color} strokeWidth={strokeWidth} strokeLinejoin="round" />;

      case 'close':
        // Lucide: x
        return <Path d="M18 6L6 18M6 6l12 12" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />;

      case 'check':
        // Lucide: check
        return <Polyline points="20 6 9 17 4 12" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />;

      case 'refresh-cw':
        return (
          <>
            <Path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
            <Path d="M3 3v5h5" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
          </>
        );

      case 'upload':
        // Lucide: upload-cloud
        return (
          <>
            <Path d="M16 16l-4-4-4 4M12 12v9" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
            <Path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
          </>
        );

      case 'plus':
        // Lucide: plus
        return <Path d="M12 5v14M5 12h14" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />;

      case 'copy':
        // Lucide: copy
        return (
          <>
            <Rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
            <Path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
          </>
        );

      case 'send':
        // Lucide: send
        return <Path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />;

      case 'download':
        // Lucide: download
        return (
          <>
            <Path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
          </>
        );

      case 'transfers':
        // Lucide: arrow-up-down
        return <Path d="M17 4v16M17 4l-4 4M17 4l4 4M7 20V4M7 20l4-4M7 20l-4-4" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />;

      case 'info':
        // Lucide: info
        return (
          <>
            <Circle cx="12" cy="12" r="10" stroke={color} strokeWidth={strokeWidth} />
            <Path d="M12 16v-4M12 8h.01" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
          </>
        );

      case 'file':
        // Lucide: file
        return (
          <>
            <Path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
            <Polyline points="14 2 14 8 20 8" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
          </>
        );

      case 'folder':
        // Lucide: folder
        return <Path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />;

      case 'zap':
        // Lucide: zap
        return <Polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" fill={color} />;

      case 'calendar':
        // Lucide: calendar
        return (
          <>
            <Rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
            <Line x1="16" y1="2" x2="16" y2="6" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
            <Line x1="8" y1="2" x2="8" y2="6" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
            <Line x1="3" y1="10" x2="21" y2="10" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
          </>
        );

      case 'camera':
        // Lucide: camera
        return (
          <>
            <Path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
            <Circle cx="12" cy="13" r="4" stroke={color} strokeWidth={strokeWidth} />
          </>
        );

      case 'chevron-right':
        return <Path d="M9 18l6-6-6-6" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />;

      case 'chevron-down':
        return <Path d="M6 9l6 6 6-6" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />;

      case 'lock':
        // Lucide: lock
        return (
          <>
            <Rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
            <Path d="M7 11V7a5 5 0 0 1 10 0v4" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
          </>
        );

      case 'users':
        // Lucide: users
        return (
          <>
            <Path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
            <Circle cx="9" cy="7" r="4" stroke={color} strokeWidth={strokeWidth} />
            <Path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
          </>
        );

      case 'globe':
        // Lucide: globe
        return (
          <>
            <Circle cx="12" cy="12" r="10" stroke={color} strokeWidth={strokeWidth} />
            <Path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
          </>
        );

      case 'link':
        // Lucide: link
        return (
          <>
            <Path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
            <Path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
          </>
        );

      case 'magnet':
        // Lucide: magnet
        return (
          <>
            <Path d="M6 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h2" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
            <Path d="M18 15h2a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2h-2" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
            <Path d="M4 2v10" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
            <Path d="M20 2v10" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
            <Path d="M12 20v2" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
            <Path d="M12 14a4 4 0 0 1-4-4V2h8v10a4 4 0 0 1-4 4z" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
          </>
        );

      case 'trash':
        // Lucide: trash-2
        return <Path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />;

      case 'x-twitter':
        return <Path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill={color} />;

      case 'github':
        return <Path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z" fill={color} />;

      case 'discord':
        return <Path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994.021-.041.001-.09-.041-.106a13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.893.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.028zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" fill={color} />;

      case 'telegram':
        return <Path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />;

      case 'reddit':
        return (
          <G transform="scale(0.046875)">
            <Path d="M373 138.6c-25.2 0-46.3-17.5-51.9-41-30.6 4.3-54.2 30.7-54.2 62.4l0 .2c47.4 1.8 90.6 15.1 124.9 36.3 12.6-9.7 28.4-15.5 45.5-15.5 41.3 0 74.7 33.4 74.7 74.7 0 29.8-17.4 55.5-42.7 67.5-2.4 86.8-97 156.6-213.2 156.6S45.5 410.1 43 323.4c-25.4-11.9-43-37.7-43-67.7 0-41.3 33.4-74.7 74.7-74.7 17.2 0 33 5.8 45.7 15.6 34-21.1 76.8-34.4 123.7-36.4l0-.3c0-44.3 33.7-80.9 76.8-85.5 4.9-24.2 26.3-42.4 52.1-42.4 29.4 0 53.3 23.9 53.3 53.3s-23.9 53.3-53.3 53.3zM157.5 255.3c-20.9 0-38.9 20.8-40.2 47.9s17.1 38.1 38 38.1 36.6-9.8 37.8-36.9-14.7-49.1-35.7-49.1l.1 0zM395 303.1c-1.2-27.1-19.2-47.9-40.2-47.9s-36.9 22-35.7 49.1 16.9 36.9 37.8 36.9 39.3-11 38-38.1l.1 0zm-60.1 70.8c1.5-3.6-1-7.7-4.9-8.1-23-2.3-47.9-3.6-73.8-3.6s-50.8 1.3-73.8 3.6c-3.9 .4-6.4 4.5-4.9 8.1 12.9 30.8 43.3 52.4 78.7 52.4s65.8-21.6 78.7-52.4z" fill={color} />
          </G>
        );

      case 'linkedin':
        return (
          <G transform="translate(1.5, 0) scale(0.046875)">
            <Path d="M100.3 448l-92.9 0 0-299.1 92.9 0 0 299.1zM53.8 108.1C24.1 108.1 0 83.5 0 53.8 0 39.5 5.7 25.9 15.8 15.8s23.8-15.8 38-15.8 27.9 5.7 38 15.8 15.8 23.8 15.8 38c0 29.7-24.1 54.3-53.8 54.3zM447.9 448l-92.7 0 0-145.6c0-34.7-.7-79.2-48.3-79.2-48.3 0-55.7 37.7-55.7 76.7l0 148.1-92.8 0 0-299.1 89.1 0 0 40.8 1.3 0c12.4-23.5 42.7-48.3 87.9-48.3 94 0 111.3 61.9 111.3 142.3l0 164.3-.1 0z" fill={color} />
          </G>
        );

      case 'image':
        // Lucide: image
        return (
          <>
            <Rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke={color} strokeWidth={strokeWidth} />
            <Circle cx="8.5" cy="8.5" r="1.5" stroke={color} strokeWidth={strokeWidth} />
            <Path d="M21 15l-5-5L5 21" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
          </>
        );

      case 'video':
        // Lucide: video
        return (
          <>
            <Path d="M23 7l-7 5 7 5V7z" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
            <Rect x="1" y="5" width="15" height="14" rx="2" ry="2" stroke={color} strokeWidth={strokeWidth} />
          </>
        );

      case 'music':
        // Lucide: music
        return (
          <>
            <Path d="M9 18V5l12-2v13" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
            <Circle cx="6" cy="18" r="3" stroke={color} strokeWidth={strokeWidth} />
            <Circle cx="18" cy="16" r="3" stroke={color} strokeWidth={strokeWidth} />
          </>
        );

      case 'file-text':
        // Lucide: file-text
        return (
          <>
            <Path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
            <Polyline points="14 2 14 8 20 8" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
            <Line x1="16" y1="13" x2="8" y2="13" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
            <Line x1="16" y1="17" x2="8" y2="17" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
            <Polyline points="10 9 9 9 8 9" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
          </>
        );

      case 'archive':
        // Lucide: archive
        return (
          <>
            <Polyline points="21 8 21 21 3 21 3 8" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
            <Rect x="1" y="3" width="22" height="5" rx="1" stroke={color} strokeWidth={strokeWidth} />
            <Line x1="10" y1="12" x2="14" y2="12" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
          </>
        );

      case 'eye':
        // Lucide: eye
        return (
          <>
            <Path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
            <Circle cx="12" cy="12" r="3" stroke={color} strokeWidth={strokeWidth} />
          </>
        );

      case 'logo':
        // Bitdrop Official Vector Glyph Mark
        return (
          <>
            <Path d="M600 139.6c-112.8 8.1-214 54.2-292.8 133.3-28.9 29.1-51.4 58.6-73.6 96.4-43.9 75-65.3 157.3-63.3 243.2.7 28.2 1.8 40.9 5.7 64.1 18.1 107.9 74.5 207.9 157.5 279 74.7 64 162.8 101.8 258.9 110.9 20.7 2 60.5 2 81.2 0 55-5.2 111.3-21.3 160.1-45.8 14.4-7.3 19.3-11.5 23.1-19.7 7.2-15.5-1.3-34.9-17.4-39.6-9.1-2.6-15-1.6-28.1 4.9-42 21.1-87.1 34.9-131.3 40.2-18.6 2.3-58.3 3.1-76.2 1.6-75.3-6.3-145.5-33.1-207.5-79.2C344 890 302.4 840.4 273.2 782c-22.8-45.5-36.1-89.8-41.8-139.5-2.4-20.7-2.4-59.8 0-79.5 8.1-67.5 29.9-127.3 65.9-181.5 45.2-67.8 107.7-120 180.3-150.5 47.6-20 92.7-30.2 140.4-31.6 69.6-2.2 138.4 15 197 49.1 19.1 11.1 25.4 12.6 36.2 8.5 7.6-2.8 12.6-7.3 15.9-14.4 5.3-11.1 3.2-25.4-4.9-33.9-5.5-5.8-36.3-22.5-62.2-33.7-39.9-17.3-79.9-27.9-126.5-33.6-12.5-1.5-61-2.7-73.5-1.8" fill={color} />
            <Path d="M599 282.1c-61.7 6.1-121.7 30.4-172.9 70.1-12.5 9.8-41.9 38.7-51.5 50.8-71.4 89.7-89.1 217.4-45.4 326C370.1 830.4 458.3 905.6 565 929.8c54.4 12.4 116.2 9.2 167.5-8.8 24.1-8.4 28.5-11.1 33.8-20.8 10.4-19.1-3.8-43.2-25.5-43.2-3.5 0-10.3 1.8-22.7 6-31.8 10.7-58.9 14.7-93 13.7-50.5-1.5-94.9-16.5-140.1-47.5-50.9-34.9-85.5-81.3-105.3-141.4-28-84.7-12-174.2 44.1-246.7 9.9-12.8 32.1-34.5 45.7-44.7 38.9-29.1 80.7-46.3 130-53.5 13.7-2 49.2-1.7 64 .5 23.7 3.6 46.8 10.3 66.5 19.2 7.9 3.6 9.2 3.9 17.5 3.9 8 0 9.5-.3 13.5-2.7 10.5-6.2 15.5-14.9 15.4-26.8 0-7.7-1.6-12.4-6.8-19.3-9-12.2-55.9-28.8-97.1-34.3-13.4-1.8-60.1-2.6-73.5-1.3" fill={color} />
            <Path d="M615.5 419.6c-25.9 2.6-46.9 8.6-69.5 19.9-51.5 25.8-89.1 74.7-100.5 130.9-7 34.4-5.1 67.8 5.6 100 21.2 63.4 74.4 111.9 138.4 126.2 8.6 2 15.1 2.6 29.6 3.1 18.3.6 18.7.5 24-1.9 7.6-3.5 11.1-6.7 14.9-14 3-5.5 3.4-7.2 3.4-13.3 0-8.7-2.8-15.5-8.7-21.3-7.2-7-11.8-8.4-30.7-9.3-11.3-.5-18.9-1.4-24.2-2.8-33.1-8.7-61.2-30.6-79.8-62.1-5.1-8.7-10.8-23-13.7-34.6-2.5-9.9-2.7-12.6-2.8-29.9 0-20.8 1-27.2 6.7-43.5 7-19.7 16.6-35 31.2-49.6 18.6-18.6 40.5-30.5 66.1-35.9 10.9-2.4 34.3-2.4 44.5-.2 13.7 3.1 23.3 1.6 30.8-4.9 18.2-15.5 11.9-45-11.1-51.9-11.7-3.5-41.3-6.2-54.2-4.9" fill={color} />
            <Path d="M1135.3 486.7c-1.3.2-11 3.4-21.5 7-10.6 3.5-35.7 12-55.8 18.8-154.4 52.2-214.9 72.9-340.9 116.6-17.2 6-33 11.9-35 13.1-7.9 4.7-11.1 9.2-11.1 15.4 0 5.4 6.9 11.7 18 16.6 4.7 2 14.4 6.4 21.5 9.7 7.2 3.2 25.6 11.6 41 18.6 15.4 6.9 29.2 13.8 30.6 15.1 1.5 1.4 3.4 4.2 4.3 6.2.8 2 5.2 23.9 9.6 48.7 11.6 64.7 11.5 64.1 14.5 68.1 5.2 6.7 14.1 9.1 22.3 5.9 2.8-1.1 10.8-9 30.1-29.5 14.5-15.3 27-28.1 27.8-28.5 5.1-1.9 8-.5 25.9 12.1 43.5 30.8 63.6 44.6 66.8 45.9 4.4 1.9 10.7 1.9 15 .1 6.9-2.9 8.2-5.2 30.6-55.1 5.9-13.2 19-42.2 29-64.5s24-53.3 31-69c11.2-25 34.8-77.3 59-130.6 8.9-19.7 9.5-21.7 8-27.3-2.4-9-11.8-15.1-20.7-13.4M1024.1 585c1 .6 1.9 1.9 1.9 2.8 0 1.1-15.1 15.2-37.7 35.2-20.8 18.4-47 41.9-58.4 52.1-11.4 10.3-34.2 30.6-50.6 45.2l-29.9 26.6-8.4 22.6c-6 16-8.7 22.1-9.2 20.8-.7-1.8-12.8-69.5-12.8-71.8 0-1.8 199.7-134.1 202.8-134.5.2 0 1.2.5 2.3 1" fill={color} />
          </>
        );

      default:
        return null;
    }
  };

  const viewBox = name === 'logo' ? '170.17 139.29 986.46 928.71' : '0 0 24 24';

  return (
    <Svg width={size} height={size} viewBox={viewBox} fill="none">
      {renderIconContent()}
    </Svg>
  );
};
